import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import {
  EVAC_ZONES_EDIT_FILL_LAYER_ID,
  PENDING_EVAC_ZONES_EDIT_LAYER_ID_PREFIX,
} from 'features/mapping/components/layers/StructuredEvacuationsLayer/constants';
import { getActiveZonesLayerIds } from 'features/mapping/components/layers/StructuredEvacuationsLayer/utils';
import { useKeyboardOpenState } from 'hooks/useKeyboardOpenState';
import { useCallback } from 'react';
import { MapMouseEvent, MarkerDragEvent, useMap } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { GeoEventTypes } from '../../../../constants';

type UseGeoEventEditMarkerProps = {
  geoEventType: GeoEventType;
  /** Memoized callback */
  onChange: (location: { lat: number; lng: number }) => void;
  disabled?: boolean;
  moveOnEvacZoneClick?: boolean; // When true, clicks landing on an evacuation zone move the marker
};

type UseGeoEventEditMarker = {
  markerProps: {
    onDragEnd: (e: MarkerDragEvent) => void;
  };
};

export const useGeoEventEditMarker = (
  props: UseGeoEventEditMarkerProps,
): UseGeoEventEditMarker => {
  const {
    geoEventType,
    onChange,
    disabled = false,
    moveOnEvacZoneClick = false,
  } = props;
  const { current: map } = useMap();
  const isKeyboardOpen = useKeyboardOpenState();

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      if (disabled || isKeyboardOpen) return;

      if (geoEventType === GeoEventTypes.location) {
        const { lat: newLat, lng: newLng } = event.lngLat;
        onChange({ lat: newLat, lng: newLng });
        map?.panTo({ lat: newLat, lng: newLng });
        return;
      }

      if (!moveOnEvacZoneClick) {
        const features = map?.queryRenderedFeatures(event.point, {
          layers: [
            EVAC_ZONES_EDIT_FILL_LAYER_ID,
            `${PENDING_EVAC_ZONES_EDIT_LAYER_ID_PREFIX}-fill`,
            ...getActiveZonesLayerIds(),
          ],
        });
        // Return early if click was on an evacuation zone
        if (features && features.length > 0) return;
      }

      const { lat: newLat, lng: newLng } = event.lngLat;
      onChange({ lat: newLat, lng: newLng });
      map?.panTo({ lat: newLat, lng: newLng });
    },
    [
      disabled,
      geoEventType,
      isKeyboardOpen,
      map,
      moveOnEvacZoneClick,
      onChange,
    ],
  );

  useMapEvent('click', handleClick, map, true);

  const handleDrag = (event: MarkerDragEvent): void => {
    const { lat: newLat, lng: newLng } = event.lngLat;
    onChange({ lat: newLat, lng: newLng });
  };

  return { markerProps: { onDragEnd: handleDrag } };
};
